import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { canOpenPortalJob } from '../../utils/portalJobNavigation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, InputGroup, Spinner, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { deleteData, fetchData, patchData, postData } from '../../redux/slices/apiCallSlice';
import { fetchBackgroundData, fullScheduleRequest } from '../../redux/api/portalApi';
import {
  selectDropdownOptions,
} from '../../redux/slices/dropdownSlice';
import {
  clearJobSchedule,
  fetchJobSchedule,
  updateJob,
} from '../../redux/slices/jobScheduleSlice';
import { selectUserDetails } from '../../redux/slices/userSlice';
import useWeekCalculations from '../../functions/useWeekCalculations';
import useAssemblyScheduleChangeLog from '../../functions/useAssemblyScheduleChangeLog';
import { groupJobsByShipDate } from '../JobSchedulePage/utils/tableUtils';
import { filterJobsForSchedule } from '../JobSchedulePage/utils/globalSearchUtils';
import SaveBanner from '../JobDetailPage/Mechanical/SaveBanner';
import { formatAssemblyIssueStatusAuditValue } from './assemblyScheduleChangeLogUtils';
import AssemblyEditModal from './AssemblyEditModal';
import AssemblyExportModal from './AssemblyExportModal';
import AssemblyIssuesCommentsModal from './AssemblyIssuesCommentsModal';
import AssemblyMultiSelect from './AssemblyMultiSelect';
import AssemblyViewControls from './AssemblyViewControls';
import {
  exportAssemblyScheduleToExcel,
  exportAssemblyScheduleToPdf,
  printAssemblySchedule,
} from './assemblyExportUtils';
import {
  ASSEMBLY_COLUMNS,
  ASSEMBLY_DETAIL_COLUMNS,
  ASSEMBLY_FIELD_BY_KEY,
  ASSEMBLY_PRIMARY_COLUMNS,
  ASSEMBLY_SCHEDULE_LIST_CONTRACT,
  ASSEMBLY_VIEW_CATEGORY,
  DEFAULT_ASSEMBLY_COLUMN_VISIBILITY,
  buildAssemblyScheduleMutation,
  buildAssemblyScheduleListUrl,
  formatAssemblyDate,
  formatAssemblyDateForInput,
  formatAssemblyIssueComments,
  getAssemblyDueCompletionValue,
  getAssemblyIssueCommentText,
  getAssemblyRecordId,
  getAssemblyRecordProjectId,
  getAssemblyIssueCommentProjectId,
  getDropdownDisplayText,
  getDropdownOptionsForTypes,
  getIssuesCommentsClass,
  getModelHighlightClass,
  getPackingListStatusClass,
  getProjectStatusClass,
  isAssemblyIssueComplete,
  mergeUniqueOptionLabels,
  normalizeAssemblyIssueComments,
  normalizeAssemblyRecords,
  normalizeAssemblyScheduleRows,
  normalizeText,
  parseAssemblyMultiSelectValue,
} from './assemblyScheduleConfig';
import './assembly.css';
import { useRealtimeResource, useRealtimeState } from '../../realtime/RealtimeProvider';
import { normalizeFullSchedulePayload } from '../../redux/slices/jobScheduleSlice';

const DEFAULT_FILTERS = Object.freeze({
  search: '',
  projectStatus: 'all',
  packingListStatus: 'all',
  packingListOwner: 'all',
});

const STICKY_IDENTITY_COLUMN_WIDTHS_REM = Object.freeze({
  JobNumber: 8.5,
  ModelNumber: 8.5,
  ProjectName: 18,
});

const getRecordMatch = (records, job) => records.find((record) => {
  const projectId = getAssemblyRecordProjectId(record);
  if (projectId !== null && projectId !== undefined && job?.ID !== null && job?.ID !== undefined) {
    return String(projectId) === String(job.ID);
  }
  return normalizeText(record?.JobNumber).toLowerCase() === normalizeText(job?.JobNumber).toLowerCase();
});

const getWeekDescription = (week, weekColumns) => {
  const match = String(week).match(/^Week (\d+)$/);
  if (!match) return week;
  const definition = weekColumns[Number(match[1]) - 1];
  if (!definition) return week;
  const format = (value) => new Date(value).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
  });
  return `${week} · ${format(definition.startDate)} – ${format(definition.endDate)}`;
};

const normalizeSavedView = (view) => {
  try {
    return JSON.parse(view?.Value ?? view?.value ?? '{}');
  } catch (_) {
    return null;
  }
};

const AssemblySchedule = ({ canEdit }) => {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const dropdownOptions = useSelector(selectDropdownOptions);
  const userDetails = useSelector(selectUserDetails);
  const jobs = useSelector((state) => state.jobSchedule.data || []);
  const loading = useSelector((state) => state.jobSchedule.loading);
  const scheduleError = useSelector((state) => state.jobSchedule.error);
  const currentUser = userDetails?.localAccountId;
  const { weekColumns } = useWeekCalculations(52);
  const { logAssemblyScheduleChange } = useAssemblyScheduleChangeLog();

  const [assemblyRecords, setAssemblyRecords] = useState([]);
  const [assemblyProjectRows, setAssemblyProjectRows] = useState([]);
  const [assemblyComments, setAssemblyComments] = useState([]);
  const [assemblyDataWarning, setAssemblyDataWarning] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSavingModal, setIsSavingModal] = useState(false);
  const [modalError, setModalError] = useState('');
  const [selectedIssuesJob, setSelectedIssuesJob] = useState(null);
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [updatingCommentId, setUpdatingCommentId] = useState(null);
  const [commentError, setCommentError] = useState('');
  const [notification, setNotification] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [weeksToShow, setWeeksToShow] = useState(3);
  const [columnVisibility, setColumnVisibility] = useState({
    ...DEFAULT_ASSEMBLY_COLUMN_VISIBILITY,
  });
  const [savedViews, setSavedViews] = useState([]);
  const [isSavingView, setIsSavingView] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const defaultViewLoaded = useRef(false);
  const { mode: realtimeMode } = useRealtimeState();
  const [realtimeJobs, setRealtimeJobs] = useState(null);
  useEffect(() => { setRealtimeJobs(null); }, [jobs, apiUrls.fullSchedule]);
  const realtimeGuard = useRef({});
  realtimeGuard.current = { urls: apiUrls, blocked: loading || isRefreshing || Boolean(editingCell) || savingCell
    || showEditModal || isSavingModal || Boolean(selectedIssuesJob) || isSavingComment || Boolean(updatingCommentId) };
  useRealtimeResource({ enabled: ['session', 'domain'].includes(realtimeMode),
    ownerKey: `assembly:${currentUser}:${apiUrls.assemblySchedule}:${apiUrls.fullSchedule}`,
    resources: ['portal:assembly-schedule', 'portal:assembly-issues-comments', 'portal:general-project-info', 'portal:full-schedule', 'portal:soldto-shipto-info'],
    blocked: realtimeGuard.current.blocked,
    refresh: async ({ signal }) => {
      const valid = () => !signal.aborted && realtimeGuard.current.urls === apiUrls && !realtimeGuard.current.blocked;
      if (!valid() || !apiUrls.assemblySchedule || !apiUrls.fullSchedule) return false;
      const read = (url, contract = {}) => dispatch(fetchBackgroundData({ url, contract, signal }));
      const first = await read(buildAssemblyScheduleListUrl(apiUrls.assemblySchedule, 1), ASSEMBLY_SCHEDULE_LIST_CONTRACT);
      const pages = Math.max(1, Number(first?.pagination?.totalPages) || 1);
      if (!Number.isSafeInteger(pages) || pages > 1000) return false;
      const remaining = await Promise.all(Array.from({ length: pages - 1 }, (_, i) =>
        read(buildAssemblyScheduleListUrl(apiUrls.assemblySchedule, i + 2), ASSEMBLY_SCHEDULE_LIST_CONTRACT)));
      if ([first, ...remaining].some(page => !Array.isArray(page) && !Array.isArray(page?.data) && !Array.isArray(page?.value))) return false;
      const schedule = await dispatch(fetchBackgroundData({ ...fullScheduleRequest(apiUrls.fullSchedule), signal }));
      if (!schedule || typeof schedule !== 'object' || Object.values(schedule).some(row =>
        !row || !(row.GeneralProjectInfo || row.generalProjectInfo))) return false;
      const rows = [first, ...remaining].flatMap(normalizeAssemblyScheduleRows);
      const comments = apiUrls.assemblyIssuesComments ? await read(apiUrls.assemblyIssuesComments) : rows;
      if (!valid()) return false;
      setRealtimeJobs(normalizeFullSchedulePayload(schedule).jobs);
      setAssemblyProjectRows(rows);
      setAssemblyRecords(normalizeAssemblyRecords(rows));
      setAssemblyComments(normalizeAssemblyIssueComments(comments));
      return true;
    },
  });

  useEffect(() => {
    if (!notification) return undefined;
    const timeout = window.setTimeout(() => setNotification(null), 3000);
    return () => window.clearTimeout(timeout);
  }, [notification]);

  const loadAssemblyRecords = useCallback(async () => {
    if (!apiUrls.assemblySchedule) return;
    try {
      const firstPagePayload = await dispatch(fetchData({
        url: buildAssemblyScheduleListUrl(apiUrls.assemblySchedule, 1),
        contract: ASSEMBLY_SCHEDULE_LIST_CONTRACT,
      })).unwrap();
      const totalPages = Math.max(1, Number(firstPagePayload?.pagination?.totalPages) || 1);
      const remainingPagePayloads = totalPages > 1
        ? await Promise.all(Array.from({ length: totalPages - 1 }, (_, index) => (
            dispatch(fetchData({
              url: buildAssemblyScheduleListUrl(apiUrls.assemblySchedule, index + 2),
              contract: ASSEMBLY_SCHEDULE_LIST_CONTRACT,
            })).unwrap()
          )))
        : [];
      const scheduleRows = [firstPagePayload, ...remainingPagePayloads]
        .flatMap(normalizeAssemblyScheduleRows);
      setAssemblyProjectRows(scheduleRows);
      setAssemblyRecords(normalizeAssemblyRecords(scheduleRows));

      const nestedComments = normalizeAssemblyIssueComments(scheduleRows);
      if (!apiUrls.assemblyIssuesComments) {
        setAssemblyComments(nestedComments);
        setAssemblyDataWarning('');
        return;
      }

      try {
        const commentsPayload = await dispatch(fetchData({
          url: apiUrls.assemblyIssuesComments,
        })).unwrap();
        const combinedComments = [
          ...nestedComments,
          ...normalizeAssemblyIssueComments(commentsPayload),
        ];
        const seen = new Set();
        setAssemblyComments(combinedComments.filter((comment) => {
          const key = comment.ID ?? comment.id ?? JSON.stringify(comment);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }));
        setAssemblyDataWarning('');
      } catch (commentsError) {
        console.error('Unable to load assembly issues/comments:', commentsError);
        setAssemblyComments(nestedComments);
        setAssemblyDataWarning('Assembly issues/comments are temporarily unavailable.');
      }
    } catch (error) {
      console.error('Unable to load assembly schedule workflow records:', error);
      setAssemblyRecords([]);
      setAssemblyProjectRows([]);
      setAssemblyComments([]);
      setAssemblyDataWarning(
        'Assembly workflow fields are temporarily unavailable. Core production schedule data is still shown.'
      );
    }
  }, [apiUrls.assemblyIssuesComments, apiUrls.assemblySchedule, dispatch]);

  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      dispatch(clearJobSchedule());
      await Promise.all([
        dispatch(fetchJobSchedule()).unwrap(),
        loadAssemblyRecords(),
      ]);
    } catch (error) {
      console.error('Unable to refresh Assembly Schedule:', error);
      setNotification({ variant: 'danger', message: 'Unable to refresh the Assembly Schedule.' });
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch, loadAssemblyRecords]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  useGuardedRealtimeList({ apiKey: 'userPreferences', enabled: Boolean(currentUser),
    apply: rows => setSavedViews(rows.filter(row => (row.Category ?? row.category) === ASSEMBLY_VIEW_CATEGORY && (row.UserObjectID ?? row.userObjectID) === currentUser)),
  });

  const fetchSavedViews = useCallback(async () => {
    if (!apiUrls.userPreferences || !currentUser) return;
    try {
      const payload = await dispatch(fetchData({ url: apiUrls.userPreferences })).unwrap();
      const preferences = Array.isArray(payload) ? payload : [];
      setSavedViews(preferences.filter((preference) => (
        (preference.Category ?? preference.category) === ASSEMBLY_VIEW_CATEGORY &&
        (preference.UserObjectID ?? preference.userObjectID) === currentUser
      )));
    } catch (error) {
      console.error('Unable to load Assembly Schedule views:', error);
    }
  }, [apiUrls.userPreferences, currentUser, dispatch]);

  useEffect(() => {
    fetchSavedViews();
  }, [fetchSavedViews]);

  const loadView = useCallback((view, notify = true) => {
    const config = normalizeSavedView(view);
    if (!config) {
      setNotification({ variant: 'danger', message: 'That Assembly Schedule view could not be loaded.' });
      return;
    }
    setFilters({ ...DEFAULT_FILTERS, ...(config.filters || {}) });
    const savedWeeks = Number(config.weeksToShow);
    setWeeksToShow([1, 2, 3, 4, 6, 12].includes(savedWeeks) ? savedWeeks : 3);
    setColumnVisibility({
      ...DEFAULT_ASSEMBLY_COLUMN_VISIBILITY,
      ...(config.columnVisibility || {}),
    });
    if (notify) {
      setNotification({ variant: 'success', message: `Loaded view “${view.Key}”.` });
    }
  }, []);

  useEffect(() => {
    if (defaultViewLoaded.current || savedViews.length === 0) return;
    defaultViewLoaded.current = true;
    const defaultView = savedViews.find((view) => ['default', 'default view'].includes(
      normalizeText(view.Key).toLowerCase()
    ));
    if (defaultView) loadView(defaultView, false);
  }, [loadView, savedViews]);

  const commentsByProject = useMemo(() => assemblyComments.reduce((map, comment) => {
    const projectId = getAssemblyIssueCommentProjectId(comment);
    if (projectId === null || projectId === undefined) return map;
    const key = String(projectId);
    map.set(key, [...(map.get(key) || []), comment]);
    return map;
  }, new Map()), [assemblyComments]);

  const assemblyProjectRowsById = useMemo(() => new Map(
    assemblyProjectRows
      .filter((row) => row?.ID !== null && row?.ID !== undefined)
      .map((row) => [String(row.ID), row])
  ), [assemblyProjectRows]);

  const mergedJobs = useMemo(() => (realtimeJobs || jobs).map((job) => {
    const assemblyRecord = getRecordMatch(assemblyRecords, job);
    const assemblyProjectRow = assemblyProjectRowsById.get(String(job.ID));
    const comments = (commentsByProject.get(String(job.ID)) || [])
      .filter((comment) => !isAssemblyIssueComplete(comment));
    const issuesComments = formatAssemblyIssueComments(comments);
    if (!assemblyRecord && !assemblyProjectRow && !issuesComments) return job;
    return {
      ...job,
      SalesRep: assemblyProjectRow?.SalesRep ?? job.SalesRep,
      ProjectManager: assemblyProjectRow?.ProjectManager ?? job.ProjectManager,
      MechanicalEngineer: assemblyProjectRow?.MechanicalEngineer ?? job.MechanicalEngineer,
      ElectricalEngineer: assemblyProjectRow?.ElectricalEngineer ?? job.ElectricalEngineer,
      Assembler: assemblyProjectRow?.Assembler ??
        assemblyProjectRow?.Assemblers ??
        job.Assembler ??
        job.Assemblers,
      AssemblyType: assemblyProjectRow?.AssemblyType ?? job.AssemblyType,
      ...(assemblyRecord || {}),
      AssemblyExpectedCompletion: job.AssemblyExpectedCompletion ??
        assemblyProjectRow?.AssemblyExpectedCompletion,
      ...(issuesComments ? { IssuesComments: issuesComments } : {}),
      ID: job.ID,
      ProjectID: job.ID,
      AssemblyScheduleID: getAssemblyRecordId(assemblyRecord),
    };
  }), [assemblyProjectRowsById, assemblyRecords, commentsByProject, jobs, realtimeJobs]);

  const assemblyEligibleJobs = useMemo(
    () => filterJobsForSchedule(mergedJobs, ''),
    [mergedJobs]
  );

  const staticProjectStatusOptions = useMemo(
    () => getDropdownOptionsForTypes(dropdownOptions, ['Assembly Project Status']),
    [dropdownOptions]
  );
  const staticPackingOptions = useMemo(
    () => getDropdownOptionsForTypes(dropdownOptions, ['Packing List Status']),
    [dropdownOptions]
  );
  const staticPackingTypeOptions = useMemo(
    () => getDropdownOptionsForTypes(dropdownOptions, ['Packing List Type']),
    [dropdownOptions]
  );
  const staticPackingOwnerOptions = useMemo(
    () => getDropdownOptionsForTypes(dropdownOptions, ['Packing List Owner']),
    [dropdownOptions]
  );
  const staticAssemblerOptions = useMemo(
    () => getDropdownOptionsForTypes(dropdownOptions, ['Assembler', 'Assembly Assembler'])
      .sort((left, right) => (
        getDropdownDisplayText(left).localeCompare(getDropdownDisplayText(right), undefined, {
          sensitivity: 'base',
        })
      )),
    [dropdownOptions]
  );
  const staticAssemblyTypeOptions = useMemo(
    () => getDropdownOptionsForTypes(dropdownOptions, ['Assembly Type']),
    [dropdownOptions]
  );
  const staticIssueStatusOptions = useMemo(
    () => getDropdownOptionsForTypes(dropdownOptions, [
      'Assembly Issue Status',
      'Assembly Issues Status',
      'Assembly Issues Comments Status',
      'Issue Status',
    ]),
    [dropdownOptions]
  );

  const dropdownOptionsByField = useMemo(() => {
    return {
      AssemblyProjectStatus: mergeUniqueOptionLabels(
        staticProjectStatusOptions,
        assemblyEligibleJobs.map((job) => job.AssemblyProjectStatus)
      ),
      PackingListType: mergeUniqueOptionLabels(
        staticPackingTypeOptions,
        assemblyEligibleJobs.map((job) => job.PackingListType)
      ),
      PackingListStatus: mergeUniqueOptionLabels(
        staticPackingOptions,
        assemblyEligibleJobs.map((job) => job.PackingListStatus)
      ),
      PackingListOwner: mergeUniqueOptionLabels(
        staticPackingOwnerOptions,
        assemblyEligibleJobs.map((job) => job.PackingListOwner)
      ),
      Assembler: mergeUniqueOptionLabels(
        staticAssemblerOptions,
        assemblyEligibleJobs.flatMap((job) => parseAssemblyMultiSelectValue(job.Assembler))
      ),
      AssemblyType: mergeUniqueOptionLabels(
        staticAssemblyTypeOptions,
        assemblyEligibleJobs.map((job) => job.AssemblyType)
      ),
      IssueCommentStatus: mergeUniqueOptionLabels(
        staticIssueStatusOptions,
        assemblyComments.map((comment) => comment.Status)
      ),
    };
  }, [
    assemblyEligibleJobs,
    assemblyComments,
    staticAssemblerOptions,
    staticAssemblyTypeOptions,
    staticIssueStatusOptions,
    staticPackingOptions,
    staticPackingOwnerOptions,
    staticPackingTypeOptions,
    staticProjectStatusOptions,
  ]);

  const filteredJobs = useMemo(() => {
    const search = normalizeText(filters.search).toLowerCase();
    return assemblyEligibleJobs.filter((job) => {
      if (job.ActualShipDate) return false;
      if (search && ![
        job.JobNumber,
        job.ModelNumber,
        job.ProjectName,
        job.IssuesComments,
        job.JobLocation,
        job.Notes,
      ].some((value) => normalizeText(value).toLowerCase().includes(search))) {
        return false;
      }
      if (filters.projectStatus !== 'all' && job.AssemblyProjectStatus !== filters.projectStatus) return false;
      if (filters.packingListStatus !== 'all' && job.PackingListStatus !== filters.packingListStatus) return false;
      if (filters.packingListOwner !== 'all' && job.PackingListOwner !== filters.packingListOwner) return false;
      return true;
    });
  }, [assemblyEligibleJobs, filters]);

  const { groupedJobs, sortedWeeks } = useMemo(() => {
    const grouped = groupJobsByShipDate(filteredJobs, weekColumns);
    const scheduled = Object.keys(grouped)
      .filter((week) => /^Week \d+$/.test(week) && grouped[week].length > 0)
      .sort((left, right) => Number(left.replace('Week ', '')) - Number(right.replace('Week ', '')));
    const visibleScheduled = weeksToShow === 999 ? scheduled : scheduled.slice(0, Number(weeksToShow));
    const ordered = [];
    if (grouped['Past Due']?.length) ordered.push('Past Due');
    ordered.push(...visibleScheduled);
    return { groupedJobs: grouped, sortedWeeks: ordered };
  }, [filteredJobs, weekColumns, weeksToShow]);

  const visiblePrimaryColumns = useMemo(
    () => ASSEMBLY_PRIMARY_COLUMNS.filter((column) => columnVisibility[column.key] !== false),
    [columnVisibility]
  );
  const visibleDetailColumns = useMemo(
    () => ASSEMBLY_DETAIL_COLUMNS.filter((column) => columnVisibility[column.key] !== false),
    [columnVisibility]
  );
  const stickyIdentityColumnStyles = useMemo(() => {
    let leftOffset = 0;
    return visiblePrimaryColumns.reduce((styles, column) => {
      const width = STICKY_IDENTITY_COLUMN_WIDTHS_REM[column.key];
      if (!width) return styles;
      styles[column.key] = {
        left: `${leftOffset}rem`,
        minWidth: `${width}rem`,
        width: `${width}rem`,
        maxWidth: `${width}rem`,
      };
      leftOffset += width;
      return styles;
    }, {});
  }, [visiblePrimaryColumns]);
  const exportColumns = useMemo(() => {
    const seen = new Set();
    return ASSEMBLY_COLUMNS.filter((column) => {
      if (seen.has(column.key)) return false;
      seen.add(column.key);
      return true;
    });
  }, []);
  const primaryColSpan = visiblePrimaryColumns.length + 2;

  const saveJobChanges = useCallback(async (job, changes) => {
    const assemblyChanges = {};
    const generalProjectChanges = {};

    Object.entries(changes).forEach(([field, value]) => {
      const definition = ASSEMBLY_FIELD_BY_KEY[field];
      if (!definition?.editable) return;
      const previousComparableValue = definition.type === 'date'
        ? formatAssemblyDateForInput(job[field])
        : String(job[field] ?? '').trim();
      const nextComparableValue = definition.type === 'date'
        ? formatAssemblyDateForInput(value)
        : String(value ?? '').trim();
      if (previousComparableValue === nextComparableValue) return;

      const normalizedValue = value || null;
      if (definition.source === 'assembly') assemblyChanges[field] = normalizedValue;
      if (definition.source === 'generalProject') generalProjectChanges[field] = normalizedValue;
    });

    const logSuccessfulChanges = (successfulChanges) => Promise.all(
      Object.entries(successfulChanges).map(([field, newValue]) => (
        logAssemblyScheduleChange(
          job.ID,
          job.JobNumber,
          job.ProjectName,
          field,
          job[field],
          newValue
        )
      ))
    );

    if (Object.keys(assemblyChanges).length > 0) {
      const existingRecord = getRecordMatch(assemblyRecords, job);
      const mutation = buildAssemblyScheduleMutation(existingRecord, job.ID, assemblyChanges);
      const { payload } = mutation;
      let response;
      if (mutation.method === 'patch') {
        response = await dispatch(patchData({
          url: apiUrls.assemblySchedule,
          data: payload,
          allowFullPayload: true,
        })).unwrap();
      } else {
        response = await dispatch(postData({
          url: apiUrls.assemblySchedule,
          data: payload,
        })).unwrap();
      }

      const responseRecord = response?.data && !Array.isArray(response.data)
        ? response.data
        : response;
      const nextRecord = {
        ...(existingRecord || {}),
        ...payload,
        ...(responseRecord && typeof responseRecord === 'object' && !Array.isArray(responseRecord)
          ? responseRecord
          : {}),
      };
      setAssemblyRecords((current) => {
        const existingIndex = current.findIndex((record) => record === existingRecord);
        if (existingIndex === -1) return [...current, nextRecord];
        return current.map((record, index) => index === existingIndex ? nextRecord : record);
      });
      await logSuccessfulChanges(assemblyChanges);
    }

    if (Object.keys(generalProjectChanges).length > 0) {
      if (!apiUrls.generalProject) throw new Error('General project API URL is unavailable.');
      await dispatch(patchData({
        url: `${apiUrls.generalProject}?id=${job.ID}`,
        data: {
          ID: job.ID,
          ...generalProjectChanges,
        },
      })).unwrap();
      dispatch(updateJob({ ID: job.ID, ...generalProjectChanges }));
      await logSuccessfulChanges(generalProjectChanges);
    }
  }, [
    apiUrls.generalProject,
    apiUrls.assemblySchedule,
    assemblyRecords,
    dispatch,
    logAssemblyScheduleChange,
  ]);

  const beginInlineEdit = (job, column) => {
    if (!canEdit || !column.editable) return;
    setEditingCell({ jobId: job.ID, field: column.key });
    setEditValue(column.type === 'date'
      ? formatAssemblyDateForInput(job[column.key])
      : (job[column.key] ?? ''));
  };

  const cancelInlineEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const commitInlineEdit = async (job, column) => {
    if (!editingCell || savingCell) return;
    const originalValue = column.type === 'date'
      ? formatAssemblyDateForInput(job[column.key])
      : String(job[column.key] ?? '');
    if (String(editValue ?? '') === originalValue) {
      cancelInlineEdit();
      return;
    }

    setSavingCell(true);
    try {
      await saveJobChanges(job, { [column.key]: editValue });
      setNotification({ variant: 'success', message: `${column.label} updated for ${job.JobNumber}.` });
      cancelInlineEdit();
    } catch (error) {
      console.error('Unable to update Assembly Schedule cell:', error);
      setNotification({ variant: 'danger', message: `Unable to update ${column.label}.` });
    } finally {
      setSavingCell(false);
    }
  };

  const handleInlineKeyDown = (event, job, column) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelInlineEdit();
    }
    if (event.key === 'Enter' && column.type !== 'textarea') {
      event.preventDefault();
      commitInlineEdit(job, column);
    }
  };

  const getCellClassName = (job, column) => {
    if (column.key === 'ModelNumber') return getModelHighlightClass(job);
    if (column.key === 'IssuesComments') return getIssuesCommentsClass(job.IssuesComments);
    if (column.key === 'AssemblyProjectStatus') return getProjectStatusClass(job.AssemblyProjectStatus);
    if (column.key === 'PackingListStatus') return getPackingListStatusClass(job.PackingListStatus);
    return '';
  };

  const renderBaseDisplayValue = (job, column) => {
    const value = job[column.key];
    if (column.type === 'date') return formatAssemblyDate(value) || '—';
    return normalizeText(value) || '—';
  };

  const renderDisplayValue = (job, column) => {
    const displayValue = renderBaseDisplayValue(job, column);
    const completionValue = column.type === 'date'
      ? getAssemblyDueCompletionValue(job, column.key)
      : null;

    if (!completionValue) return displayValue;

    return (
      <span className="assembly-date-value">
        <span>{displayValue}</span>
        <i
          className="bi bi-check-square-fill assembly-complete-check"
          title={`Completed ${formatAssemblyDate(completionValue) || normalizeText(completionValue)}`}
          aria-label="Completed"
        />
      </span>
    );
  };

  const renderEditableValue = (job, column, compact = false) => {
    const isEditing = editingCell?.jobId === job.ID && editingCell?.field === column.key;
    if (!isEditing) {
      return (
        <span
          className={`assembly-cell-value ${column.editable && canEdit ? 'is-editable' : ''}`}
          onDoubleClick={() => {
            if (column.editable && canEdit) beginInlineEdit(job, column);
            else openEditModal(job);
          }}
          title={normalizeText(job[column.key]) || column.label}
        >
          {renderDisplayValue(job, column)}
        </span>
      );
    }

    const options = dropdownOptionsByField[column.key] || [];
    const isAssemblyStatus = column.key === 'AssemblyProjectStatus';
    const isPackingListStatus = column.key === 'PackingListStatus';
    const getOptionStatusClass = (value) => {
      if (isAssemblyStatus) return getProjectStatusClass(value);
      if (isPackingListStatus) return getPackingListStatusClass(value);
      return '';
    };
    const commonProps = {
      autoFocus: true,
      value: editValue,
      disabled: savingCell,
      onChange: (event) => setEditValue(event.target.value),
      onBlur: () => commitInlineEdit(job, column),
      onKeyDown: (event) => handleInlineKeyDown(event, job, column),
      size: 'sm',
      className: [
        compact ? 'assembly-inline-compact' : '',
        getOptionStatusClass(editValue),
      ].filter(Boolean).join(' '),
    };

    if (['dropdown', 'user-dropdown', 'sales-dropdown'].includes(column.type)) {
      const hasValue = options.some((option) => option === editValue);
      return (
        <Form.Select {...commonProps}>
          <option value="">Select...</option>
          {options.map((option) => (
            <option
              key={option}
              value={option}
              className={getOptionStatusClass(option)}
            >
              {option}
            </option>
          ))}
          {!hasValue && editValue && (
            <option
              value={editValue}
              className={getOptionStatusClass(editValue)}
            >
              {editValue}
            </option>
          )}
        </Form.Select>
      );
    }

    if (column.type === 'multi-dropdown') {
      return (
        <AssemblyMultiSelect
          value={editValue}
          options={options}
          onChange={setEditValue}
          disabled={savingCell}
          forceOpen
          onApply={() => commitInlineEdit(job, column)}
          onCancel={cancelInlineEdit}
        />
      );
    }

    return <Form.Control {...commonProps} type={column.type === 'date' ? 'date' : 'text'} />;
  };

  const openIssuesCommentsModal = (job) => {
    setSelectedIssuesJob(job);
    setCommentError('');
  };

  const renderPrimaryCellValue = (job, column) => {
    if (column.key === 'JobNumber') {
      const isPortalJob = canOpenPortalJob(job);
      if (!isPortalJob) return renderEditableValue(job, column);
      return (
        <a
          className="assembly-job-link"
          href={`/${job.ID}/generalInfo`}
          title={`Open ${job.JobNumber} in the Production Portal`}
        >
          {normalizeText(job.JobNumber) || '—'}
        </a>
      );
    }
    if (column.key !== 'IssuesComments') return renderEditableValue(job, column);

    const comments = (commentsByProject.get(String(job.ID)) || [])
      .filter((comment) => !isAssemblyIssueComplete(comment));
    return (
      <button
        type="button"
        className="assembly-comments-button"
        onClick={() => openIssuesCommentsModal(job)}
        title="View or add assembly issues and comments"
      >
        <span>{normalizeText(job.IssuesComments) || '—'}</span>
        <span className="assembly-comments-count" aria-label={`${comments.length} active issues or comments`}>
          {comments.length}
        </span>
      </button>
    );
  };

  const toggleRow = (jobId) => {
    setExpandedRows((current) => {
      const next = new Set(current);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const openEditModal = (job) => {
    setSelectedJob(job);
    setModalError('');
    setShowEditModal(true);
  };

  const saveModal = async (formData) => {
    if (!selectedJob) return;
    const changes = {};
    ASSEMBLY_COLUMNS.forEach((column) => {
      if (!column.editable) return;
      const originalValue = column.type === 'date'
        ? formatAssemblyDateForInput(selectedJob[column.key])
        : String(selectedJob[column.key] ?? '');
      if (String(formData[column.key] ?? '') !== originalValue) {
        changes[column.key] = formData[column.key];
      }
    });

    if (Object.keys(changes).length === 0) {
      setShowEditModal(false);
      return;
    }

    setIsSavingModal(true);
    setModalError('');
    try {
      await saveJobChanges(selectedJob, changes);
      setNotification({ variant: 'success', message: `Assembly schedule updated for ${selectedJob.JobNumber}.` });
      setShowEditModal(false);
      setSelectedJob(null);
    } catch (error) {
      console.error('Unable to save Assembly Schedule modal:', error);
      setModalError(error?.message || 'Unable to save the Assembly Schedule changes.');
    } finally {
      setIsSavingModal(false);
    }
  };

  const addIssueComment = async ({ Author, Description, Status }) => {
    if (!selectedIssuesJob || !apiUrls.assemblyIssuesComments) {
      setCommentError('The assembly issues/comments endpoint is unavailable.');
      return false;
    }

    setIsSavingComment(true);
    setCommentError('');
    try {
      const response = await dispatch(postData({
        url: apiUrls.assemblyIssuesComments,
        data: {
          ProjectID: selectedIssuesJob.ID,
          Author,
          Description,
          Status,
        },
      })).unwrap();
      const responseComments = normalizeAssemblyIssueComments(response);
      const savedComment = responseComments[0] || {
        ProjectID: selectedIssuesJob.ID,
        Author,
        Description,
        Status,
        CreatedDate: new Date().toISOString(),
      };
      const normalizedComment = {
        ...savedComment,
        ProjectID: getAssemblyIssueCommentProjectId(savedComment) ?? selectedIssuesJob.ID,
      };

      setAssemblyComments((current) => [...current, normalizedComment]);
      setNotification({
        variant: 'success',
        message: `Comment added for ${selectedIssuesJob.JobNumber}.`,
      });
      return true;
    } catch (error) {
      console.error('Unable to add assembly issue/comment:', error);
      setCommentError(error?.message || 'Unable to add the issue/comment.');
      return false;
    } finally {
      setIsSavingComment(false);
    }
  };

  const updateIssueCommentStatus = async (comment, nextStatus) => {
    const commentId = getAssemblyRecordId(comment);
    if (!commentId || !apiUrls.assemblyIssuesComments) {
      setCommentError('This issue/comment cannot be updated because its record ID is unavailable.');
      return false;
    }

    const projectId = getAssemblyIssueCommentProjectId(comment) ?? selectedIssuesJob?.ID;
    const author = normalizeText(
      comment?.Author ??
      comment?.CreatedBy ??
      userDetails?.name ??
      userDetails?.displayName ??
      userDetails?.username
    );
    const description = getAssemblyIssueCommentText(comment);
    const previousStatus = normalizeText(comment?.Status ?? comment?.status);

    setUpdatingCommentId(commentId);
    setCommentError('');
    try {
      const response = await dispatch(patchData({
        url: apiUrls.assemblyIssuesComments,
        data: {
          ID: commentId,
          ProjectID: projectId,
          Author: author,
          Description: description,
          Status: nextStatus,
        },
        allowFullPayload: true,
      })).unwrap();
      const responseComments = normalizeAssemblyIssueComments(response);
      const updatedComment = {
        ...comment,
        ...(responseComments[0] || {}),
        ID: commentId,
        ProjectID: projectId,
        Author: author,
        Description: description,
        Status: nextStatus,
      };

      setAssemblyComments((current) => current.map((item) => (
        String(getAssemblyRecordId(item)) === String(commentId) ? updatedComment : item
      )));
      await logAssemblyScheduleChange(
        projectId,
        selectedIssuesJob?.JobNumber,
        selectedIssuesJob?.ProjectName,
        'IssueCommentStatus',
        formatAssemblyIssueStatusAuditValue({
          commentId,
          status: previousStatus,
          description,
        }),
        formatAssemblyIssueStatusAuditValue({
          commentId,
          status: nextStatus,
          description,
        })
      );
      setNotification({
        variant: 'success',
        message: `Issue/comment status updated to ${nextStatus}.`,
      });
      return true;
    } catch (error) {
      console.error('Unable to update assembly issue/comment status:', error);
      setCommentError(error?.message || 'Unable to update the issue/comment status.');
      return false;
    } finally {
      setUpdatingCommentId(null);
    }
  };

  const saveView = async ({ name, description }) => {
    if (!apiUrls.userPreferences || !currentUser) return false;
    setIsSavingView(true);
    try {
      const value = JSON.stringify({ filters, weeksToShow, columnVisibility, description });
      await dispatch(postData({
        url: apiUrls.userPreferences,
        data: {
          userObjectID: currentUser,
          category: ASSEMBLY_VIEW_CATEGORY,
          key: name,
          value,
        },
      })).unwrap();
      await fetchSavedViews();
      setNotification({ variant: 'success', message: `Saved view “${name}”.` });
      return true;
    } catch (error) {
      console.error('Unable to save Assembly Schedule view:', error);
      setNotification({ variant: 'danger', message: 'Unable to save the Assembly Schedule view.' });
      return false;
    } finally {
      setIsSavingView(false);
    }
  };

  const deleteView = async (view) => {
    const viewId = view.ID ?? view.id;
    if (!viewId) return;
    try {
      await dispatch(deleteData({ url: apiUrls.userPreferences, deleteId: viewId })).unwrap();
      await fetchSavedViews();
      setNotification({ variant: 'success', message: `Deleted view “${view.Key}”.` });
    } catch (error) {
      console.error('Unable to delete Assembly Schedule view:', error);
      setNotification({ variant: 'danger', message: 'Unable to delete the Assembly Schedule view.' });
    }
  };

  const handleExport = ({ format, selectedColumns }) => {
    const columns = exportColumns.filter((column) => selectedColumns[column.key]);
    const context = {
      columns,
      sortedWeeks,
      groupedJobs,
      getWeekLabel: (week) => getWeekDescription(week, weekColumns),
    };

    try {
      if (format === 'print') printAssemblySchedule(context);
      else if (format === 'pdf') exportAssemblyScheduleToPdf(context);
      else exportAssemblyScheduleToExcel(context);
      setShowExportModal(false);
      setNotification({
        variant: 'success',
        message: format === 'print'
          ? 'Assembly Schedule print preview opened.'
          : `Assembly Schedule ${format.toUpperCase()} export created.`,
      });
    } catch (error) {
      console.error('Unable to export the Assembly Schedule:', error);
      setNotification({
        variant: 'danger',
        message: error?.message || 'Unable to export the Assembly Schedule.',
      });
    }
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="assembly-loading">
        <Spinner animation="border" variant="primary" />
        <p>Loading Assembly Schedule...</p>
      </div>
    );
  }

  return (
    <div className="assembly-schedule">
      <SaveBanner
        showNotification={Boolean(notification)}
        saveStatus={notification?.variant === 'danger'
          ? `Failed: ${notification.message}`
          : (notification?.message || '')}
      />
      {assemblyDataWarning && <Alert variant="warning">{assemblyDataWarning}</Alert>}
      {scheduleError && <Alert variant="danger">Unable to load the production schedule: {scheduleError}</Alert>}

      <div className="assembly-toolbar">
        <div className="assembly-toolbar-search">
          <InputGroup>
            <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
            <Form.Control
              value={filters.search}
              onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
              placeholder="Search job, model, project, issue, location, or notes"
            />
          </InputGroup>
        </div>
        <AssemblyViewControls
          columnVisibility={columnVisibility}
          savedViews={savedViews}
          isSavingView={isSavingView}
          onColumnVisibilityChange={setColumnVisibility}
          onSaveView={saveView}
          onLoadView={loadView}
          onDeleteView={deleteView}
        />
        <Button variant="outline-secondary" onClick={() => setShowExportModal(true)}>
          <i className="bi bi-printer me-2" />Export / Print
        </Button>
        <Button
          variant="outline-primary"
          onClick={refreshData}
          disabled={isRefreshing || loading}
          title="Refresh Assembly Schedule"
        >
          <i className={`bi bi-arrow-repeat ${isRefreshing ? 'assembly-spin' : ''}`} />
        </Button>
      </div>

      <div className="assembly-filter-row">
        <Form.Group>
          <Form.Label>Assembly status</Form.Label>
          <Form.Select
            value={filters.projectStatus}
            onChange={(event) => setFilters((current) => ({ ...current, projectStatus: event.target.value }))}
            className={filters.projectStatus === 'all'
              ? ''
              : getProjectStatusClass(filters.projectStatus)}
          >
            <option value="all">All assembly statuses</option>
            {dropdownOptionsByField.AssemblyProjectStatus.map((option) => (
              <option key={option} value={option} className={getProjectStatusClass(option)}>
                {option}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Packing list status</Form.Label>
          <Form.Select
            value={filters.packingListStatus}
            onChange={(event) => setFilters((current) => ({ ...current, packingListStatus: event.target.value }))}
            className={filters.packingListStatus === 'all'
              ? ''
              : getPackingListStatusClass(filters.packingListStatus)}
          >
            <option value="all">All packing list statuses</option>
            {dropdownOptionsByField.PackingListStatus.map((option) => (
              <option key={option} value={option} className={getPackingListStatusClass(option)}>
                {option}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Packing list owner</Form.Label>
          <Form.Select
            value={filters.packingListOwner}
            onChange={(event) => setFilters((current) => ({ ...current, packingListOwner: event.target.value }))}
          >
            <option value="all">All packing list owners</option>
            {dropdownOptionsByField.PackingListOwner.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="assembly-weeks-filter">
          <Form.Label>Weeks to show</Form.Label>
          <Form.Select value={weeksToShow} onChange={(event) => setWeeksToShow(Number(event.target.value))}>
            {[1, 2, 3, 4, 6, 12].map((count) => <option key={count} value={count}>{count}</option>)}
          </Form.Select>
        </Form.Group>
      </div>

      <div className="assembly-table-wrapper">
        <Table bordered hover size="sm" className="assembly-table">
          <thead>
            <tr>
              <th className="assembly-expand-column" aria-label="Expand row" />
              {visiblePrimaryColumns.map((column) => (
                <th
                  key={column.key}
                  className={stickyIdentityColumnStyles[column.key]
                    ? 'assembly-sticky-identity-column'
                    : undefined}
                  style={stickyIdentityColumnStyles[column.key]}
                >
                  {column.label}
                </th>
              ))}
              <th className="assembly-actions-column">Edit</th>
            </tr>
          </thead>
          <tbody>
            {sortedWeeks.map((week) => (
              <React.Fragment key={week}>
                <tr className="assembly-week-row">
                  <td colSpan={primaryColSpan}>
                    <span className="assembly-week-title">
                      {getWeekDescription(week, weekColumns)}
                    </span>
                    <span>{groupedJobs[week].length} job{groupedJobs[week].length === 1 ? '' : 's'}</span>
                  </td>
                </tr>
                {groupedJobs[week].map((job) => {
                  const rowId = job.ID ?? job.JobNumber;
                  const expanded = expandedRows.has(rowId);
                  return (
                    <React.Fragment key={rowId}>
                      <tr className={expanded ? 'assembly-primary-row is-expanded' : 'assembly-primary-row'}>
                        <td className="assembly-expand-column">
                          <button
                            type="button"
                            className="assembly-expand-button"
                            onClick={() => toggleRow(rowId)}
                            aria-expanded={expanded}
                            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${job.JobNumber}`}
                          >
                            <i className={`bi bi-chevron-${expanded ? 'down' : 'right'}`} />
                          </button>
                        </td>
                        {visiblePrimaryColumns.map((column) => (
                          <td
                            key={column.key}
                            className={[
                              getCellClassName(job, column),
                              stickyIdentityColumnStyles[column.key]
                                ? 'assembly-sticky-identity-column'
                                : '',
                            ].filter(Boolean).join(' ')}
                            style={stickyIdentityColumnStyles[column.key]}
                          >
                            {renderPrimaryCellValue(job, column)}
                          </td>
                        ))}
                        <td className="assembly-actions-column">
                          <Button
                            size="sm"
                            variant={canEdit ? 'outline-primary' : 'outline-secondary'}
                            onClick={() => openEditModal(job)}
                            title={canEdit ? 'Edit assembly schedule' : 'View assembly schedule'}
                          >
                            <i className={`bi bi-${canEdit ? 'pencil-square' : 'eye'}`} />
                          </Button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="assembly-detail-row">
                          <td colSpan={primaryColSpan}>
                            {visibleDetailColumns.length === 0 ? (
                              <span className="text-muted">All expanded-row columns are hidden in this view.</span>
                            ) : (
                              <div className="assembly-detail-table-wrapper">
                                <table className="assembly-detail-table">
                                  <caption className="visually-hidden">
                                    Expanded assembly schedule details for {job.JobNumber}
                                  </caption>
                                  <thead>
                                    <tr>
                                      {visibleDetailColumns.map((column) => (
                                        <th key={column.key} scope="col">{column.label}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      {visibleDetailColumns.map((column) => (
                                        <td key={column.key} className={getCellClassName(job, column)}>
                                          {renderEditableValue(job, column, true)}
                                        </td>
                                      ))}
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
            {sortedWeeks.length === 0 && (
              <tr>
                <td colSpan={primaryColSpan} className="text-center text-muted py-5">
                  No assembly jobs match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <AssemblyEditModal
        show={showEditModal}
        job={selectedJob}
        canEdit={canEdit}
        dropdownOptionsByField={dropdownOptionsByField}
        isSaving={isSavingModal}
        error={modalError}
        onHide={() => {
          if (isSavingModal) return;
          setShowEditModal(false);
          setSelectedJob(null);
          setModalError('');
        }}
        onSave={saveModal}
      />
      <AssemblyIssuesCommentsModal
        show={Boolean(selectedIssuesJob)}
        job={selectedIssuesJob}
        comments={selectedIssuesJob
          ? (commentsByProject.get(String(selectedIssuesJob.ID)) || [])
          : []}
        author={normalizeText(
          userDetails?.name ?? userDetails?.displayName ?? userDetails?.username
        )}
        statusOptions={dropdownOptionsByField.IssueCommentStatus || []}
        canEdit={canEdit}
        isSaving={isSavingComment}
        updatingCommentId={updatingCommentId}
        error={commentError}
        onHide={() => {
          if (isSavingComment || updatingCommentId) return;
          setSelectedIssuesJob(null);
          setCommentError('');
        }}
        onAddComment={addIssueComment}
        onUpdateStatus={updateIssueCommentStatus}
      />
      <AssemblyExportModal
        show={showExportModal}
        columns={exportColumns}
        defaultVisibility={columnVisibility}
        onHide={() => setShowExportModal(false)}
        onExport={handleExport}
      />
    </div>
  );
};

export default AssemblySchedule;
